import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ghostTextByMode,
  wildPrompts,
  VISUAL_STYLES,
  DEFAULT_STYLE,
  type VisualStyle,
  type ProductionTarget,
} from './hero-content'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { StyleLibraryModal } from '../canvas-v2/nodes/shared/StyleLibraryModal'

interface HeroSectionProps {
  onGenerate: (
    prompt: string,
    style: VisualStyle,
    productionTarget: ProductionTarget,
  ) => void
  isCreating?: boolean
  onStyleChange?: (style: VisualStyle, productionTarget: ProductionTarget) => void
}

export default function HeroSection({ onGenerate, isCreating = false, onStyleChange }: HeroSectionProps) {
  const [prompt, setPrompt] = useState('')
  const [outputMode, setOutputMode] = useLocalStorage<'video' | 'webtoon' | 'creative'>('landing-output-mode', 'video')
  const [selectedStyle, setSelectedStyle] = useLocalStorage<VisualStyle>('landing-visual-style', DEFAULT_STYLE)
  const productionTarget: ProductionTarget =
    outputMode === 'webtoon' ? 'webtoon' : 'video'

  useEffect(() => {
    onStyleChange?.(selectedStyle, productionTarget)
  }, [selectedStyle, productionTarget, onStyleChange])
  const [isStyleLibraryOpen, setIsStyleLibraryOpen] = useState(false)
  const [ghostText, setGhostText] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-grow textarea to fit content
  const autoGrow = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [])

  useEffect(() => {
    autoGrow()
  }, [prompt, autoGrow])

  // Ghost text typing animation
  useEffect(() => {
    if (prompt.length > 0) {
      setGhostText('')
      return
    }

    const ideas = ghostTextByMode[outputMode]
    let ideaIndex = 0
    let charIndex = 0
    let isDeleting = false
    let isPaused = false

    const type = () => {
      if (prompt.length > 0) {
        setGhostText('')
        return
      }

      const currentIdea = ideas[ideaIndex]

      if (isPaused) return

      if (!isDeleting) {
        setGhostText(currentIdea.substring(0, charIndex + 1))
        charIndex++

        if (charIndex === currentIdea.length) {
          isPaused = true
          setTimeout(() => {
            isPaused = false
            isDeleting = true
          }, 2000)
        }
      } else {
        setGhostText(currentIdea.substring(0, charIndex - 1))
        charIndex--

        if (charIndex === 0) {
          isDeleting = false
          ideaIndex = (ideaIndex + 1) % ideas.length
        }
      }
    }

    const interval = setInterval(type, isDeleting ? 30 : 50)
    return () => clearInterval(interval)
  }, [prompt, outputMode])

  const handleGenerate = () => {
    if (prompt.trim()) {
      onGenerate(prompt.trim(), selectedStyle, productionTarget)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleGenerate()
    }
  }

  const styleLabel = VISUAL_STYLES.find(s => s.value === selectedStyle)?.label ?? 'Look'

  return (
    <>
    <main className="relative z-[1] max-w-[900px] w-full px-4 sm:px-6 pt-[min(20vh,140px)] sm:pt-[min(25vh,180px)] pb-8 sm:pb-12 mx-auto flex flex-col items-center text-center">
      <h1 className="text-[clamp(2rem,6vw,3.5rem)] font-semibold mb-3 tracking-[-0.03em] text-foreground leading-[1.1] text-center">
        Share your story
      </h1>
      <p className="text-base sm:text-lg text-text-muted mb-5 max-w-[500px] leading-relaxed text-center">
        Create cinematic content with AI, from prompt to final cut.
      </p>

      {/* Input Container - Bolt.new inspired */}
      <div className="w-full max-w-[820px] mb-4 sm:mb-6">
        {/* Vercel-style Glass Tabs */}
        <div className="flex justify-center mb-3">
          <div className="vercel-tabs" data-active={outputMode}>
            <div className="vercel-tab-pill" />
            <button
              onClick={() => setOutputMode('webtoon')}
              className={`vercel-tab ${outputMode === 'webtoon' ? 'active' : ''}`}
            >
              Webtoon
            </button>
            <button
              onClick={() => setOutputMode('video')}
              className={`vercel-tab ${outputMode === 'video' ? 'active' : ''}`}
            >
              Video story
            </button>
          </div>
        </div>

        <div className="prompt-glow-wrapper">
          <div className="relative bg-background border border-border rounded-md sm:rounded-lg p-3 sm:p-4 transition-all duration-200">
          {/* Textarea with prefix and ghost text */}
          <div className="relative">
            <div className="flex items-start gap-1 text-base sm:text-base leading-relaxed">
              <span className={`whitespace-nowrap flex-shrink-0 ${outputMode === 'webtoon' ? 'text-primary' : 'text-cyan-400'}`}>{outputMode === 'webtoon' ? 'Webtoon' : 'Video story'} –</span>
              <div className="relative flex-1">
                <textarea
                  ref={textareaRef}
                  data-testid="hero-prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  rows={2}
                  maxLength={10000}
                  placeholder=""
                  className="w-full bg-transparent border-none text-foreground text-base sm:text-base resize-none overflow-hidden outline-none font-inherit leading-relaxed relative z-[2] focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-transparent"
                />
                {prompt.length === 0 && !isFocused && (
                  <div className="absolute inset-0 pointer-events-none text-base sm:text-base leading-relaxed text-left">
                    <span className="text-[rgba(255,255,255,0.3)]">{ghostText}</span>
                    <span className="text-[rgba(255,255,255,0.5)] animate-pulse">|</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Character counter */}
          {prompt.length > 0 && (
            <div className="flex justify-end mt-1" data-testid="char-counter">
              <span className={`text-sm ${
                prompt.length >= 10000
                  ? 'text-red-400'
                  : prompt.length > 9000
                    ? 'text-yellow-400'
                    : 'text-text-muted'
              }`}>
                {prompt.length.toLocaleString()} / 10,000
              </span>
            </div>
          )}

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-[rgba(255,255,255,0.06)]">
            <div className="flex items-center gap-1 flex-wrap">
              {/* Surprise Me */}
              <button
                onClick={() => {
                  const pick = wildPrompts[Math.floor(Math.random() * wildPrompts.length)]
                  setPrompt(pick)
                  textareaRef.current?.focus()
                }}
                className="flex items-center gap-1.5 text-text-muted hover:text-text-secondary py-1.5 px-2.5 rounded-lg cursor-pointer text-sm sm:text-sm transition-all duration-200 hover:bg-card-hover"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="3"/>
                  <circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none"/>
                  <circle cx="16" cy="8" r="1.5" fill="currentColor" stroke="none"/>
                  <circle cx="8" cy="16" r="1.5" fill="currentColor" stroke="none"/>
                  <circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none"/>
                  <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>
                </svg>
                <span>Surprise me</span>
              </button>

              {/* Style Library Button */}
              <button
                data-testid="style-library-button"
                onClick={() => setIsStyleLibraryOpen(true)}
                className="flex items-center gap-1.5 text-text-muted hover:text-text-secondary py-1.5 px-2.5 rounded-lg cursor-pointer text-sm sm:text-sm transition-all duration-200 hover:bg-card-hover"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19l7-7 3 3-7 7-3-3z"/>
                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
                  <path d="M2 2l7.586 7.586"/>
                  <circle cx="11" cy="11" r="2"/>
                </svg>
                <span>{styleLabel}</span>
                <span className="text-text-muted">&middot; {VISUAL_STYLES.length} styles</span>
              </button>

            </div>

            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                {/* Primary button */}
                <button
                  onClick={handleGenerate}
                  disabled={isCreating || !prompt.trim()}
                  data-testid="create-btn"
                  className="flex items-center justify-center gap-2 bg-white text-black py-2 px-4 sm:px-5 rounded-full cursor-pointer text-sm sm:text-base font-medium transition-all duration-200 hover:bg-gray-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isCreating ? (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <>
                      <span className="hidden sm:inline">Create</span>
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"/>
                        <polyline points="12 5 19 12 12 19"/>
                      </svg>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        </div>
      </div>

    </main>

      {/* Style Library Modal — rendered outside the z-[1] stacking context */}
      <StyleLibraryModal
        isOpen={isStyleLibraryOpen}
        onClose={() => setIsStyleLibraryOpen(false)}
        currentStyleId={selectedStyle}
        onSelect={(id) => setSelectedStyle(id as VisualStyle)}
      />
    </>
  )
}
